<!-- eslint-disable vue/no-v-html -->
<!--
  新クロージングページ（eod-closing-v2）専用の勤怠テーブル（インライン編集版）。

  元: src/views/v2-temp/eod-closing/components/TimecardsTable.vue
  違い: セル編集を v-menu ポップオーバー方式から「セルに常時表示の入力欄」を
  置く完全インライン編集に変更。テーブルのレイアウト・列構成・既存の保存
  ロジック（debounceUpdateTimecard / debounceUpdateBusinessRuleResult /
  debounceUpdateBreakTimecard 等）はそのまま流用している。
  既存 TimecardsTable.vue は旧クロージングページが使い続けるため変更しない。

  休憩（breakTimecards）セルについて:
  1 行に複数の休憩があり、各休憩で IN/OUT 編集・追加・削除が必要なため、
  固定幅セルへの常時表示インライン化は表示が破綻する。休憩のみ従来どおり
  v-menu（一覧 + 各休憩の編集メニュー）方式を維持する。
-->
<template>
  <div class="timecards-table-wrapper">
    <v-skeleton-loader
      v-if="isMounting"
      type="table"
    />
    <v-data-table
      v-else
      :loading="isLoading"
      :headers="headers"
      :items="items"
      :header-props="{ sortByText: 'ソート' }"
      :footer-props="{ itemsPerPageText: '行/ページ:' }"
      no-data-text="データがありません"
      loading-text="読込中..."
    >
      <template #top>
        <v-toolbar
          flat
          color="transparent"
        >
          <v-toolbar-title>勤怠</v-toolbar-title>
          <v-spacer />
          <v-chip-group
            v-model="targetRole"
            class="mr-5"
          >
            <v-chip
              v-for="(role, key) in roles"
              :key="key"
              filter
              outlined
              :value="key"
            >
              {{ role }}
            </v-chip>
          </v-chip-group>
          <CreateTimecardDialogButton
            :timecard-statuses="timecardStatuses"
            :users="targetUsers"
            :business-rule-assignments="businessRuleAssignments"
            :existing-brr-by-user-id="existingBrrByUserId"
            :disabled="isLoading"
            :date="date"
            @create="createTimecard"
          />
          <v-btn
            icon
            depressed
            :ripple="false"
            :loading="isLoading"
            class="ml-2 mr-1"
            @click="getTimecards"
          >
            <v-icon>
              {{ icons.mdiReload }}
            </v-icon>
          </v-btn>
        </v-toolbar>
      </template>

      <template
        v-for="braHeader in headers.filter(h => h.isBusinessRule)"
        #[`header.${braHeader.value}`]
      >
        <span
          :key="`hdr-${braHeader.value}`"
          draggable="true"
          class="bra-header-draggable"
          :class="{ 'bra-header-drag-over': draggedOverBraId === braHeader.businessRuleAssignmentId }"
          @dragstart="onColumnDragStart($event, braHeader)"
          @dragover.prevent="onColumnDragOver($event, braHeader)"
          @dragleave="onColumnDragLeave($event, braHeader)"
          @drop="onColumnDrop($event, braHeader)"
          @dragend="onColumnDragEnd"
        >
          {{ braHeader.text }}
          <span
            v-if="braHeader.aggregationName && hasMultipleAggregations"
            class="aggregation-badge aggregation-badge--header"
            :style="aggregationColorStyle(braHeader.aggregationId)"
          >
            {{ braHeader.aggregationName }}
          </span>
        </span>
      </template>

      <template
        v-for="header in headers"
        #[`item.${header.value}`]="{ item }"
      >
        <!-- ユーザー名（編集不可・表示のみ） -->
        <span
          v-if="header.value === 'userName'"
          :key="`cell-${header.value}-${item.rowKey}`"
        >
          {{ item.userName }}
          <template v-if="hasMultipleAggregations">
            <span
              v-for="agg in userAggregationsMap[item.userId] || []"
              :key="`agg-${item.rowKey}-${agg.id}`"
              class="aggregation-badge"
              :style="aggregationColorStyle(agg.id)"
            >
              {{ agg.name }}
            </span>
          </template>
          <br>
          <span
            v-if="item.startAt && item.leaveAt && !isInvalidWorkingDuration(item.startAt, item.leaveAt)"
            class="text--secondary"
            style="font-size: 0.7rem;"
          >
            {{ durationText(item.startAt, item.leaveAt).hoursAndMinutes }}
          </span>
        </span>

        <!-- 出勤時刻（インライン入力） -->
        <v-text-field
          v-else-if="header.value === 'startAt'"
          :key="`cell-${header.value}-${item.rowKey}`"
          v-model="item.startAt"
          type="datetime-local"
          dense
          hide-details
          :error="isInvalidWorkingDuration(item.startAt, item.leaveAt)"
          @blur="debounceUpdateTimecard(item, header)"
        />

        <!-- 退勤時刻（インライン入力） -->
        <v-text-field
          v-else-if="header.value === 'leaveAt'"
          :key="`cell-${header.value}-${item.rowKey}`"
          v-model="item.leaveAt"
          type="datetime-local"
          dense
          hide-details
          clearable
          :error="isInvalidWorkingDuration(item.startAt, item.leaveAt)"
          @focus="setDefaultDateTime(item, 'leaveAt')"
          @click="setDefaultDateTime(item, 'leaveAt')"
          @blur="debounceUpdateTimecard(item, header)"
        />

        <!-- 遅刻 / 早退（分・インライン入力） -->
        <v-text-field
          v-else-if="header.value === 'lateMinutes' || header.value === 'earlyLeaveMinutes'"
          :key="`cell-${header.value}-${item.rowKey}`"
          v-model="item[header.value]"
          type="number"
          dense
          hide-details
          @change="debounceUpdateTimecard(item, header)"
        >
          <template #append-outer>
            分
          </template>
        </v-text-field>

        <!-- ステータス（インライン v-select） -->
        <v-select
          v-else-if="header.value === 'timecardStatusId'"
          :key="`cell-${header.value}-${item.rowKey}`"
          v-model="item.timecardStatusId"
          :items="timecardStatuses"
          item-text="attributes.name"
          item-value="attributes.id"
          dense
          hide-details
          clearable
          @change="debounceUpdateTimecard(item, header)"
        />

        <!-- ビジネスルール結果列（インライン入力 / 編集不可セルは表示のみ） -->
        <template v-else-if="header.isBusinessRule">
          <v-text-field
            v-if="header.businessRule.editableResult && isApplicableCell(header, item)"
            :key="`cell-${header.value}-${item.rowKey}`"
            v-model.number="item[header.value]"
            type="number"
            dense
            hide-details
            @blur="debounceUpdateBusinessRuleResult(item, header)"
          />
          <span
            v-else
            :key="`cell-${header.value}-${item.rowKey}`"
            class="text--disabled"
          >
            {{ !isApplicableCell(header, item) ? '—' : (item[header.value] != null ? item[header.value] : '-') }}
          </span>
        </template>

        <!-- 休憩（複数行・追加削除があるため v-menu を維持） -->
        <template v-else-if="header.value === 'breakTimecards'">
          <v-menu
            :key="`cell-${header.value}-${item.rowKey}`"
            nudge-width="64"
            :close-on-content-click="false"
          >
            <template #activator="{ on: breakTimecardOn, attrs: breakTimecardAttrs }">
              <v-col
                tabindex="0"
                class="cursor-pointer pa-0"
                v-bind="breakTimecardAttrs"
                v-on="breakTimecardOn"
              >
                <v-text-field
                  v-for="(breakTimecard, id) in item.breakTimecards"
                  :key="`break-timecard-menu-${id}`"
                  :value="timeText(breakTimecard, 'startAt') + ' - ' + timeText(breakTimecard, 'endAt')"
                  readonly
                  dense
                  hide-details
                  :error="isInvalidBreakingDuration(breakTimecard.startAt, breakTimecard.endAt)"
                  v-bind="breakTimecardAttrs"
                  v-on="breakTimecardOn"
                />
                <span
                  v-if="Object.keys(item.breakTimecards).length === 0"
                  class="text--disabled"
                >
                  休憩なし
                </span>
              </v-col>
            </template>

            <template #default>
              <v-list dense>
                <v-list-item
                  v-for="(bt, btId) in item.breakTimecards"
                  :key="`break-timecard-list-item-${btId}`"
                >
                  <v-list-item-title
                    :class="{ ['error--text']: isInvalidBreakingDuration(bt.startAt, bt.endAt) }"
                  >
                    {{ timeText(bt, 'startAt') + ' - ' + timeText(bt, 'endAt') }}
                  </v-list-item-title>
                  <v-list-item-action>
                    <v-menu :close-on-content-click="false">
                      <template #activator="{ on: btEditOn, attrs: btEditAttrs }">
                        <v-btn
                          icon
                          v-bind="btEditAttrs"
                          v-on="btEditOn"
                        >
                          <v-icon small>
                            {{ icons.mdiPencilOutline }}
                          </v-icon>
                        </v-btn>
                      </template>
                      <template #default>
                        <v-card>
                          <v-card-subtitle>休憩</v-card-subtitle>
                          <v-card-text>
                            <v-row>
                              <v-col cols="12">
                                <v-text-field
                                  v-model="bt.startAt"
                                  label="IN"
                                  type="datetime-local"
                                  dense
                                  clearable
                                  autofocus
                                  :error="isInvalidBreakingDuration(bt.startAt, bt.endAt)"
                                  @blur="debounceUpdateBreakTimecard(btId, bt, item.id)"
                                  @click="setDefaultDateTime(bt, 'startAt')"
                                  @focus="setDefaultDateTime(bt, 'startAt')"
                                />
                                <v-text-field
                                  v-model="bt.endAt"
                                  label="OUT"
                                  type="datetime-local"
                                  dense
                                  clearable
                                  :error="isInvalidBreakingDuration(bt.startAt, bt.endAt)"
                                  @blur="debounceUpdateBreakTimecard(btId, bt, item.id)"
                                  @click="setDefaultDateTime(bt, 'startAt')"
                                  @focus="setDefaultDateTime(bt, 'endAt')"
                                />
                                <p class="text--secondary text-xs d-flex pr-2">
                                  <v-spacer />
                                  休憩: {{ durationText(bt.startAt, bt.endAt).hoursAndMinutes }}
                                </p>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </template>
                    </v-menu>
                  </v-list-item-action>
                  <v-list-item-action v-if="Object.keys(item.breakTimecards).length > 1">
                    <v-btn
                      icon
                      @click="destroyBreakTimecard(btId, item.id)"
                    >
                      <v-icon small>
                        {{ icons.mdiTrashCanOutline }}
                      </v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item @click="createBreakTimecard(item.id)">
                  <v-list-item-icon>
                    <v-icon small>
                      {{ icons.mdiPlus }}
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>追加</v-list-item-title>
                </v-list-item>
              </v-list>
            </template>
          </v-menu>
        </template>
      </template>

      <template #[`item.actions`]="{ item }">
        <DeleteConfirmMenu
          @confirm="destroyRow(item)"
        />
      </template>
    </v-data-table>
  </div>
</template>

<script>
import {
  ref,
  computed,
  toRefs,
  onMounted,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiReload,
  mdiTrashCanOutline,
  mdiPencilOutline,
  mdiPlus,
} from '@mdi/js'
import { format, parseISO, differenceInMinutes } from 'date-fns'
import { debounce } from 'lodash'
import themeConfig from '@themeConfig'
import TimecardApi from '@/api/v2/eod-closing/Timecard'
import BreakTimecardApi from '@/api/v2/eod-closing/BreakTimecard'
import TimecardStatusApi from '@/api/v2/eod-closing/TimecardStatus'
import BusinessRuleAssignmentApi from '@/api/v2/eod-closing/BusinessRuleAssignment'
import BusinessRuleApi from '@/api/v2/eod-closing/BusinessRule'
import BusinessRuleResultApi from '@/api/v2/eod-closing/BusinessRuleResult'
import UserApi from '@/api/waiter/User'
import useCompInit from '@/views/composable/useCompInit'
import useBusinessDateAdjustment from '@/views/v2-temp/eod-closing/composables/useBusinessDateAdjustment'
import CreateTimecardDialogButton from '@/views/v2-temp/eod-closing/components/CreateTimecardDialogButton.vue'
import DeleteConfirmMenu from '@/views/v2-temp/common/components/DeleteConfirmMenu.vue'

export default {
  name: 'TimecardsTableInline',
  components: {
    CreateTimecardDialogButton,
    DeleteConfirmMenu,
  },
  props: {
    date: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const { date } = toRefs(props)
    const { adjustDateForMidnightCrossing, buildDefaultDateTime } = useBusinessDateAdjustment(date)
    const timecards = ref([])
    const orphanBrrEntries = ref([])
    const timecardStatuses = ref([])
    const users = ref([])
    const businessRuleAssignments = ref([])
    const isMounting = ref(true)
    const targetRole = ref('cast')
    const roles = { cast: 'キャスト', waiter: 'スタッフ', alliance: 'アライアンス' }
    const baseHeaders = [
      ['userName', 'ユーザー', { width: '120px' }],
      ['startAt', '開始', { width: '210px' }],
      ['leaveAt', '終了', { width: '240px' }],
      ['breakTimecards', '休憩', { width: '160px' }],
      ['lateMinutes', '遅刻/分', { width: '140px' }],
      ['earlyLeaveMinutes', '早退/分', { width: '140px' }],
      ['timecardStatusId', 'ステータス', { width: '160px' }],
    ].map(([value, text, options = {}]) => {
      return { text, value, ...options }
    })
    const { initWith, isLoading } = useCompInit()

    const targetTimecards = computed(() => timecards.value.filter(timecard => timecard.meta.userRole === targetRole.value))
    const targetOrphanEntries = computed(() => orphanBrrEntries.value.filter(entry => entry.user?.role === targetRole.value))
    const targetUsers = computed(() => users.value.filter(user => user.attributes.role === targetRole.value))

    const targetUserIdSet = computed(() => new Set(targetUsers.value.map(u => u.attributes.id)))

    const visibleBusinessRuleAssignments = computed(() => {
      return businessRuleAssignments.value.filter(bra => {
        const userIds = bra.attributes?.userIds || []

        return userIds.some(id => targetUserIdSet.value.has(id))
      })
    })

    const headers = computed(() => {
      const brHeaders = visibleBusinessRuleAssignments.value.map(bra => {
        const br = bra.attributes.businessRule

        return {
          text: br.name,
          value: `br_${br.id}`,
          isBusinessRule: true,
          businessRule: br,
          aggregationId: bra.attributes.aggregationId,
          aggregationName: bra.attributes.aggregationName,
          businessRuleAssignmentId: Number(bra.id),
          userIds: bra.attributes.userIds || [],
          width: '140px',
          sortable: false,
        }
      })

      const actionsHeader = {
        text: '',
        value: 'actions',
        width: '80px',
        sortable: false,
      }

      return [...baseHeaders, ...brHeaders, actionsHeader]
    })

    // userId → 所属する individually_confirmable な aggregation 名の配列
    const userAggregationsMap = computed(() => {
      const map = {}
      businessRuleAssignments.value.forEach(bra => {
        const aggId = bra.attributes.aggregationId
        const aggName = bra.attributes.aggregationName
        if (!aggName) return
        const userIds = bra.attributes.userIds || []
        userIds.forEach(uid => {
          if (!map[uid]) map[uid] = new Map()
          map[uid].set(aggId, aggName)
        })
      })

      return Object.fromEntries(
        Object.entries(map).map(([uid, aggMap]) => [
          uid,
          Array.from(aggMap.entries()).map(([id, name]) => ({ id, name })),
        ]),
      )
    })

    const isApplicableCell = (header, item) => {
      if (!header.isBusinessRule) return true

      return (header.userIds || []).includes(item.userId)
    }

    // themeConfig のテーマ色を流用したカラーパレット。
    // 文字色: テーマ色そのまま / 背景色: 同色を ~12% 透過（8桁hexで指定）。
    const themeColors = themeConfig.themes.light
    const aggregationColorPalette = [
      themeColors.success,
      themeColors.info,
      themeColors.warning,
      themeColors.primary,
      themeColors.error,
      themeColors.accent,
      themeColors.secondary,
    ]

    // 表示中の集計表の「出現順」で色を割り当てるためのマップ。
    const aggregationColorMap = computed(() => {
      const map = new Map()
      let idx = 0
      businessRuleAssignments.value.forEach(bra => {
        const aggId = bra.attributes?.aggregationId
        if (aggId == null || map.has(aggId)) return
        map.set(aggId, aggregationColorPalette[idx % aggregationColorPalette.length])
        idx += 1
      })

      return map
    })

    const aggregationColorStyle = aggregationId => {
      if (aggregationId == null) return {}
      const color = aggregationColorMap.value.get(aggregationId) || aggregationColorPalette[0]

      return { backgroundColor: `${color}1F`, color }
    }

    // 集計表が複数共存している時のみチップを表示する（1つだけならノイズなので非表示）
    const hasMultipleAggregations = computed(() => aggregationColorMap.value.size > 1)

    // timecard / orphan を統合した userId → BRR配列のマップ
    const existingBrrByUserId = computed(() => {
      const map = {}
      timecards.value.forEach(tc => {
        const { userId } = tc.attributes
        const brrs = tc.attributes.businessRuleResults || []
        if (brrs.length > 0) map[userId] = brrs
      })
      orphanBrrEntries.value.forEach(entry => {
        const userId = entry.user?.id
        const brrs = entry.businessRuleResults || []
        if (userId && brrs.length > 0) map[userId] = brrs
      })

      return map
    })

    const buildBrFields = businessRuleResultsByBrId => {
      const brFields = {}
      const brOriginals = {}
      businessRuleAssignments.value.forEach(bra => {
        const brId = bra.attributes.businessRule.id
        const key = `br_${brId}`
        const value = businessRuleResultsByBrId[brId] ?? null
        brFields[key] = value
        brOriginals[key] = value
      })

      return { brFields, brOriginals }
    }

    const items = computed(() => {
      const regularItems = targetTimecards.value.map(timecard => {
        const breakTimecards = {}
        timecard.attributes.breakTimecards.data.forEach(breakTimecard => {
          const { startAt, endAt } = breakTimecard.attributes
          breakTimecards[breakTimecard.id] = { startAt, endAt }
        })

        const businessRuleResultsByBrId = {}
        const brResults = timecard.attributes.businessRuleResults || []
        brResults.forEach(brr => {
          businessRuleResultsByBrId[brr.businessRuleId] = brr.resultValue
        })

        const { brFields, brOriginals } = buildBrFields(businessRuleResultsByBrId)

        const meta = {
          userRole: roles[timecard.meta.userRole],
          _originalValues: {
            startAt: timecard.attributes.startAt,
            leaveAt: timecard.attributes.leaveAt,
            lateMinutes: timecard.attributes.lateMinutes,
            earlyLeaveMinutes: timecard.attributes.earlyLeaveMinutes,
            timecardStatusId: timecard.attributes.timecardStatusId,
            breakTimecards: JSON.parse(JSON.stringify(breakTimecards)),
            ...brOriginals,
          },
          timecardStatusName: timecard.meta.timecardStatusName,
          isVirtual: false,
          get originalValues() {
            return { ...this._originalValues }
          },
        }

        return {
          id: timecard.attributes.id,
          rowKey: `timecard-${timecard.attributes.id}`,
          userId: timecard.attributes.userId,
          userName: timecard.meta.userName,
          startAt: timecard.attributes.startAt,
          leaveAt: timecard.attributes.leaveAt,
          lateMinutes: timecard.attributes.lateMinutes,
          earlyLeaveMinutes: timecard.attributes.earlyLeaveMinutes,
          timecardStatusId: timecard.attributes.timecardStatusId,
          breakTimecards,
          ...brFields,
          meta,
        }
      })

      const orphanItems = targetOrphanEntries.value.map(entry => {
        const businessRuleResultsByBrId = {}
        const entryResults = entry.businessRuleResults || []
        entryResults.forEach(brr => {
          businessRuleResultsByBrId[brr.businessRuleId] = brr.resultValue
        })
        const { brFields, brOriginals } = buildBrFields(businessRuleResultsByBrId)

        const meta = {
          userRole: roles[entry.user.role],
          _originalValues: {
            startAt: null,
            leaveAt: null,
            lateMinutes: null,
            earlyLeaveMinutes: null,
            timecardStatusId: null,
            breakTimecards: {},
            ...brOriginals,
          },
          timecardStatusName: null,
          isVirtual: true,
          get originalValues() {
            return { ...this._originalValues }
          },
        }

        return {
          id: null,
          rowKey: `orphan-${entry.user.id}`,
          userId: entry.user.id,
          userName: entry.user.name,
          startAt: null,
          leaveAt: null,
          lateMinutes: null,
          earlyLeaveMinutes: null,
          timecardStatusId: null,
          breakTimecards: {},
          ...brFields,
          meta,
        }
      })

      return [...regularItems, ...orphanItems]
    })

    const timeText = computed(() => (resource, key) => {
      const dateTimeStr = resource[key]

      return dateTimeStr ? dateTimeStr.split('T')[1] : ''
    })

    const isEndBeforeStart = (start, end) => {
      const startDate = parseISO(start)
      const endDate = parseISO(end)
      if ([startDate, endDate].some(incomingDate => Number.isNaN(incomingDate.getTime()))) return true

      return endDate < startDate
    }

    const isInvalidWorkingDuration = computed(() => (startAt, leaveAt) => {
      if (!startAt && leaveAt) return true // 開始がない=>NG
      if (startAt && !leaveAt) return false// 終了だけない=>OK
      if (!startAt && !leaveAt) return true // 両方ない=>NG

      return isEndBeforeStart(startAt, leaveAt)
    })

    const isInvalidBreakingDuration = computed(() => (startAt, endAt) => {
      if (!startAt && endAt) return true // 開始だけない=>NG
      if (startAt && !endAt) return false // 終了だけない=>OK
      if (!startAt && !endAt) return false // 両方ない=>OK

      return isEndBeforeStart(startAt, endAt)
    })

    const durationText = computed(() => (startAt, endAt) => {
      let startDate = parseISO(startAt)
      let endDate = parseISO(endAt)

      if (isEndBeforeStart(startAt, endAt)) {
        const now = new Date()
        startDate = now
        endDate = now
      }

      // 滞在時間を計算（分単位）
      const minutes = differenceInMinutes(endDate, startDate)

      // 滞在時間を計算（時間と分の形式に変換）
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      const hoursAndMinutes = `${String(hours).padStart(2, ' ')}時間${String(remainingMinutes).padStart(2, ' ')}分`

      return {
        totalMinutes: minutes,
        hoursAndMinutes,
      }
    })

    const setDefaultDateTime = (resource, key) => {
      if (resource[key] !== null) return
      // eslint-disable-next-line no-param-reassign
      resource[key] = buildDefaultDateTime(key, resource.startAt)
    }

    // 同一ユーザーの全 timecard の businessRuleResults を上書き（targetTimecardId は除外可能）
    const propagateBusinessRuleResults = (userId, brrEntries, excludeTimecardId = null) => {
      timecards.value = timecards.value.map(tc => {
        if (tc.attributes.userId !== userId) return tc
        if (excludeTimecardId && tc.attributes.id === excludeTimecardId) return tc
        const updatedTc = JSON.parse(JSON.stringify(tc))
        updatedTc.attributes.businessRuleResults = brrEntries.map(b => ({ ...b }))

        return updatedTc
      })
    }

    // タイムカード更新
    const updateTimecard = async (item, header) => {
      try {
        const params = { [header.value]: item[header.value] }
        const res = await TimecardApi.updateTimecard(item.id, params)
        if (res.data.status === 'success') {
          const { data } = res.data.data
          const idx = timecards.value.findIndex(t => t.attributes.id === data.attributes.id)
          if (idx >= 0) {
            timecards.value.splice(idx, 1, data)
          } else {
            timecards.value.push(data)
          }
          // 同一ユーザーの他 timecard にも BRR を伝播
          const updatedBrrs = data.attributes.businessRuleResults || []
          propagateBusinessRuleResults(data.attributes.userId, updatedBrrs, data.attributes.id)
        }
      } catch (error) {
        // TODO
      }
    }
    const getTimecards = async () => {
      isLoading.value = true
      const res = await TimecardApi.getTimecards(date.value)
      if (res.data.status === 'success') {
        timecards.value = res.data.data.data
        orphanBrrEntries.value = res.data.data.orphanBrrEntries || []
      }
      isLoading.value = false
    }

    const createTimecardFromVirtual = async item => {
      try {
        isLoading.value = true
        const res = await TimecardApi.createTimecard({
          userId: item.userId,
          startAt: item.startAt,
        })
        if (res.data.status === 'success') {
          await getTimecards()
        }
      } catch (error) {
        const message = error.response?.data?.message
        if (Array.isArray(message)) {
          message.forEach(msg => vm.$toast.error(msg))
        } else if (message) {
          vm.$toast.error(message)
        }
      } finally {
        isLoading.value = false
      }
    }

    const debounceUpdateTimecard = debounce((item, header) => {
      if (header.value === 'startAt' || header.value === 'leaveAt') {
        const adjusted = adjustDateForMidnightCrossing(item[header.value])
        if (adjusted !== item[header.value]) {
          // eslint-disable-next-line no-param-reassign
          item[header.value] = adjusted
        }
        if (isInvalidWorkingDuration.value(item.startAt, item.leaveAt)) return
      }

      if (item.meta.originalValues[header.value] === item[header.value]) return

      // 仮想行で startAt を入力した場合はタイムカードを新規作成して実体化
      if (item.meta.isVirtual) {
        if (header.value === 'startAt' && item.startAt) {
          createTimecardFromVirtual(item)
        }

        return
      }

      updateTimecard(item, header)
    }, 500)

    // /タイムカード更新

    // タイムカード作成（もしくはBRRのみ作成）
    const createTimecard = async params => {
      try {
        isLoading.value = true

        if (params.startAt) {
          // タイムカード作成（BRRも同梱されていればバックエンドが保存）
          const res = await TimecardApi.createTimecard(params)
          if (res.data.status === 'success') {
            const { data } = res.data.data
            timecards.value.push(data)
            // orphanBrrEntriesから消えるよう再取得
            await getTimecards()
          }
        } else if (params.businessRuleResults?.length > 0) {
          // タイムカードなし・BRRのみ → generate_result を BR 毎に呼ぶ
          const requests = params.businessRuleResults.map(brr => {
            const bra = businessRuleAssignments.value.find(
              b => b.attributes.businessRule.id === brr.businessRuleId,
            )
            if (!bra) return Promise.resolve()

            return BusinessRuleApi.generateBusinessRuleResult({
              aggregationId: bra.attributes.aggregationId,
              contextType: 'User',
              contextId: params.userId,
              businessRuleId: brr.businessRuleId,
              resultValue: brr.resultValue,
              date: date.value,
            })
          })
          await Promise.all(requests)
          await getTimecards()
        }
      } catch (error) {
        const message = error.response?.data?.message
        if (Array.isArray(message)) {
          message.forEach(msg => vm.$toast.error(msg))
        } else if (message) {
          vm.$toast.error(message)
        }
      } finally {
        isLoading.value = false
      }
    }
    // /タイムカード作成

    // タイムカード削除
    const destroyTimecard = async id => {
      try {
        isLoading.value = true
        const res = await TimecardApi.destroyTimecard(id)
        if (res.data.status === 'success') {
          // バックエンドで関連BRRも一括削除されるため、画面全体を再取得して
          // 残行のBRR表示・orphan行を整合的に更新する
          await getTimecards()
        }
      } catch (error) {
        // TODO
      } finally {
        isLoading.value = false
      }
    }
    // /タイムカード削除

    // 仮想行削除: individually_confirmable BRR を user_id + date で一括削除
    const destroyOrphanEntry = async userId => {
      try {
        isLoading.value = true
        const res = await BusinessRuleResultApi.destroyByUserAndDate({ userId, date: date.value })
        if (res.data.status === 'success') {
          await getTimecards()
        }
      } catch (error) {
        const message = error.response?.data?.message
        if (Array.isArray(message)) {
          message.forEach(msg => vm.$toast.error(msg))
        } else if (message) {
          vm.$toast.error(message)
        }
      } finally {
        isLoading.value = false
      }
    }

    const destroyRow = item => {
      if (item.meta.isVirtual) {
        destroyOrphanEntry(item.userId)
      } else {
        destroyTimecard(item.id)
      }
    }

    // 休憩更新
    const upsertBreakTimecard = (parentTimecardId, breakTimecardData) => {
      const timecardIdx = timecards.value.findIndex(t => t.attributes.id === parentTimecardId)
      const updatedTimecard = { ...timecards.value[timecardIdx] }
      const idx = updatedTimecard.attributes.breakTimecards.data.findIndex(b => b.attributes.id === breakTimecardData.attributes.id)
      if (idx >= 0) {
        updatedTimecard.attributes.breakTimecards.data.splice(idx, 1, breakTimecardData)
      } else {
        updatedTimecard.attributes.breakTimecards.data.push(breakTimecardData)
      }
      timecards.value.splice(timecardIdx, 1, updatedTimecard)
    }
    const updateBreakTimecard = async (id, breakTimecard, parentTimecardId) => {
      try {
        const { startAt, endAt } = breakTimecard
        const breakTimecardParams = {
          id,
          breakTimeStartAt: startAt,
          breakTimeEndAt: endAt,
          timecardId: parentTimecardId,
        }
        const res = await BreakTimecardApi.updateBreakTimecard(breakTimecardParams)
        if (res.data.status === 'success') {
          const { data } = res.data.data
          upsertBreakTimecard(parentTimecardId, data)
        }
      } catch (error) {
        // TODO
      }
    }
    const debounceUpdateBreakTimecard = debounce((id, breakTimecard, parentTimecardId) => {
      const item = items.value.find(i => i.id === parentTimecardId)

      if (item) {
        const originalBreakTimecard = item.meta.originalValues.breakTimecards[id]
        if (originalBreakTimecard.startAt === breakTimecard.startAt && originalBreakTimecard.endAt === breakTimecard.endAt) return
        if (isInvalidBreakingDuration.value(breakTimecard.startAt, breakTimecard.endAt)) return

        updateBreakTimecard(id, breakTimecard, parentTimecardId)
      }
    }, 500)
    // /休憩更新

    // 休憩作成
    const createBreakTimecard = async parentTimecardId => {
      try {
        const res = await BreakTimecardApi.createBreakTimecard({ timecardId: parentTimecardId })
        if (res.data.status === 'success') {
          const { data } = res.data.data
          upsertBreakTimecard(parentTimecardId, data)
        }
      } catch (error) {
        // TODO
      }
    }
    // /休憩作成

    // 休憩削除
    const destroyBreakTimecard = async (id, parentTimecardId) => {
      try {
        const res = await BreakTimecardApi.destroyBreakTimecard(id, parentTimecardId)
        if (res.data.status === 'success') {
          const timecardIdx = timecards.value.findIndex(t => t.attributes.id === parentTimecardId)
          if (timecardIdx < 0) return
          const updatedTimecard = { ...timecards.value[timecardIdx] }
          const idx = updatedTimecard.attributes.breakTimecards.data.findIndex(b => b.attributes.id === Number(id))
          if (idx >= 0) {
            updatedTimecard.attributes.breakTimecards.data.splice(idx, 1)
            timecards.value.splice(timecardIdx, 1, updatedTimecard)
          }
        }
      } catch (error) {
        // TODO
      }
    }
    // /休憩削除

    const getBusinessRuleAssignments = async () => {
      try {
        const res = await BusinessRuleAssignmentApi.getIndividuallyConfirmableBusinessRuleAssignments()
        if (res.data.status === 'success') {
          businessRuleAssignments.value = res.data.data.data
        }
      } catch (error) {
        // 個別確定対象のBRがない場合は何もしない
      }
    }

    const draggedBraId = ref(null)
    const draggedOverBraId = ref(null)

    const onColumnDragStart = (event, header) => {
      draggedBraId.value = header.businessRuleAssignmentId
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(header.businessRuleAssignmentId))
    }

    const onColumnDragOver = (event, header) => {
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.dropEffect = 'move'
      if (draggedBraId.value && draggedBraId.value !== header.businessRuleAssignmentId) {
        draggedOverBraId.value = header.businessRuleAssignmentId
      }
    }

    const onColumnDragLeave = (event, header) => {
      if (draggedOverBraId.value === header.businessRuleAssignmentId) {
        draggedOverBraId.value = null
      }
    }

    const onColumnDragEnd = () => {
      draggedBraId.value = null
      draggedOverBraId.value = null
    }

    const onColumnDrop = async (event, targetHeader) => {
      event.preventDefault()
      const sourceId = draggedBraId.value || Number(event.dataTransfer.getData('text/plain'))
      const targetId = targetHeader.businessRuleAssignmentId
      draggedBraId.value = null
      draggedOverBraId.value = null
      if (!sourceId || sourceId === targetId) return

      const previous = [...businessRuleAssignments.value]
      const currentOrder = previous.map(bra => Number(bra.id))
      const sourceIdx = currentOrder.indexOf(Number(sourceId))
      const targetIdx = currentOrder.indexOf(Number(targetId))
      if (sourceIdx === -1 || targetIdx === -1) return

      const newOrder = [...currentOrder]
      newOrder.splice(sourceIdx, 1)
      newOrder.splice(targetIdx, 0, Number(sourceId))

      const reordered = newOrder.map(id => previous.find(b => Number(b.id) === id))
      businessRuleAssignments.value = reordered

      try {
        await BusinessRuleAssignmentApi.reorderBusinessRuleAssignments(newOrder)
      } catch (error) {
        businessRuleAssignments.value = previous
        vm.$toast.error('並び順の保存に失敗しました')
      }
    }

    const mergeUpdatedBrrs = (existingBrrs, updatedResults) => {
      const byBrId = new Map((existingBrrs || []).map(r => [r.businessRuleId, r]))
      updatedResults.forEach(entry => {
        const attrs = entry.attributes
        byBrId.set(attrs.businessRuleId, {
          businessRuleId: attrs.businessRuleId,
          resultValue: attrs.resultValue,
          isDirty: attrs.isDirty,
        })
      })

      return Array.from(byBrId.values())
    }

    const updateBusinessRuleResult = async (item, header) => {
      isLoading.value = true
      try {
        const res = await BusinessRuleApi.generateBusinessRuleResult({
          aggregationId: header.aggregationId,
          contextType: 'User',
          contextId: item.userId,
          businessRuleId: header.businessRule.id,
          resultValue: item[header.value],
          date: date.value,
        })
        if (res.data.status === 'success') {
          // レスポンスに含まれる最新のBRR値で、同一ユーザーの全timecardを更新
          const updatedResults = res.data.data.data
          timecards.value = timecards.value.map(tc => {
            if (tc.attributes.userId !== item.userId) return tc
            const updatedTimecard = JSON.parse(JSON.stringify(tc))
            updatedTimecard.attributes.businessRuleResults = mergeUpdatedBrrs(
              updatedTimecard.attributes.businessRuleResults,
              updatedResults,
            )

            return updatedTimecard
          })
          // 仮想行（タイムカードなし）の編集にも対応するため orphanBrrEntries も更新
          orphanBrrEntries.value = orphanBrrEntries.value.map(entry => {
            if (entry.user?.id !== item.userId) return entry

            return {
              ...entry,
              businessRuleResults: mergeUpdatedBrrs(entry.businessRuleResults, updatedResults),
            }
          })
        }
      } catch (error) {
        error.response?.data?.message?.split?.('\n')?.forEach(msg => {
          vm.$toast.error(msg)
        })
      } finally {
        isLoading.value = false
      }
    }

    const debounceUpdateBusinessRuleResult = debounce((item, header) => {
      if (!header.businessRule?.editableResult) return
      if (item.meta.originalValues[header.value] === item[header.value]) return
      updateBusinessRuleResult(item, header)
    }, 500)

    const getTimecardStatuses = async () => {
      isLoading.value = true
      const res = await TimecardStatusApi.getTimecardStatus(date.value)
      if (res.data.status === 'success') timecardStatuses.value = res.data.data.data
      isLoading.value = false
    }

    const getUsers = async () => {
      const res = await UserApi.getUsers()
      if (res?.data) {
        users.value = [...res.data.users.data]
      }
    }

    onMounted(async () => {
      await initWith([
        getTimecards(),
        getTimecardStatuses(),
        getUsers(),
        getBusinessRuleAssignments(),
      ])
      isMounting.value = false
    })

    return {
      isLoading,
      isMounting,
      headers,
      items,
      timeText,
      durationText,
      isInvalidWorkingDuration,
      isInvalidBreakingDuration,
      timecards,
      timecardStatuses,
      users,
      roles,
      targetRole,
      targetUsers,
      existingBrrByUserId,
      userAggregationsMap,
      isApplicableCell,
      aggregationColorStyle,
      hasMultipleAggregations,

      getTimecards,
      createTimecard,
      setDefaultDateTime,
      destroyTimecard,
      destroyRow,
      debounceUpdateTimecard,
      debounceUpdateBreakTimecard,
      debounceUpdateBusinessRuleResult,
      createBreakTimecard,
      destroyBreakTimecard,
      businessRuleAssignments,

      draggedOverBraId,
      onColumnDragStart,
      onColumnDragOver,
      onColumnDragLeave,
      onColumnDragEnd,
      onColumnDrop,

      icons: {
        mdiReload,
        mdiTrashCanOutline,
        mdiPencilOutline,
        mdiPlus,
      },
    }
  },
}
</script>

<style scoped>
.timecards-table-wrapper {
  overflow-x: auto;
}
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table {
  table-layout: fixed;
}
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > thead > tr > th,
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > tbody > tr > td {
  white-space: nowrap;
  overflow-x: auto;
}
/* セル内スクロールバーは非表示（タッチ操作などでのスクロールは可能） */
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > thead > tr > th,
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > tbody > tr > td {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > thead > tr > th::-webkit-scrollbar,
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > tbody > tr > td::-webkit-scrollbar {
  display: none;
}

/* ユーザー列を左に固定して、横スクロール時もユーザー名が見えるようにする */
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > thead > tr > th:first-child,
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > tbody > tr > td:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
}
.timecards-table-wrapper ::v-deep .v-data-table__wrapper > table > thead > tr > th:first-child {
  z-index: 2;
}
.timecards-table-wrapper ::v-deep .v-data-table.theme--light .v-data-table__wrapper > table > thead > tr > th:first-child,
.timecards-table-wrapper ::v-deep .v-data-table.theme--light .v-data-table__wrapper > table > tbody > tr > td:first-child {
  background-color: #ffffff;
}
.timecards-table-wrapper ::v-deep .v-data-table.theme--dark .v-data-table__wrapper > table > thead > tr > th:first-child,
.timecards-table-wrapper ::v-deep .v-data-table.theme--dark .v-data-table__wrapper > table > tbody > tr > td:first-child {
  background-color: #312D4B;
}
.bra-header-draggable {
  cursor: move;
  user-select: none;
  display: inline-block;
  padding: 0 4px;
}
.bra-header-drag-over {
  background-color: #e3f2fd;
  border-radius: 4px;
}
.aggregation-badge {
  display: inline-block;
  margin-left: 4px;
  padding: 1px 6px;
  font-size: 0.65rem;
  border-radius: 10px;
  vertical-align: middle;
}
.aggregation-badge--header {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}
/* インライン入力欄をセル内で詰めて表示する */
.timecards-table-wrapper ::v-deep .v-data-table__wrapper td .v-input {
  font-size: 0.875rem;
}
/* 数値入力欄のスピンボタン（上下矢印）を非表示にする */
.timecards-table-wrapper ::v-deep input[type='number'] {
  -moz-appearance: textfield;
}
.timecards-table-wrapper ::v-deep input[type='number']::-webkit-outer-spin-button,
.timecards-table-wrapper ::v-deep input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
